import { type AiBuilderChatCopy } from './aiBuilderChatCopy';
import { type ServerAiBuilderChatErrorCode } from './serverAiBuilderChatDtos';

const SERVER_AI_BUILDER_CHAT_ERROR_CODES = [
  'QUESTION_LIMIT_EXCEEDED',
  'UNSUPPORTED_URL',
  'NON_QUIZ_REQUEST',
  'HIGH_RISK_TOPIC',
  'SOURCE_REF_UNAVAILABLE',
  'AUTH_EXPIRED',
  'USAGE_BLOCKED',
  'USAGE_UNAVAILABLE',
  'SESSION_LOCKED',
  'MESSAGE_TOO_LONG',
  'INVALID_REQUEST',
  'SESSION_NOT_FOUND',
  'SESSION_BINDING_MISMATCH',
  'UNDO_UNAVAILABLE',
  'CONFIRM_TOKEN_INVALID',
  'INTERNAL_ERROR',
  'UNSUPPORTED_FORMAT',
  'EXTRACTION_FAILED',
  'FILE_TOO_LARGE',
  'LOCKED_FILE',
  'TOO_MANY_PAGES',
  'STORAGE_UPLOAD_FAILED',
  'GENERATION_EMPTY_RESULT',
  'DIRECT_GENERATE_INVALID',
] satisfies ServerAiBuilderChatErrorCode[];

export const getServerAiBuilderChatErrorMessage = (
  code: unknown,
  copy: AiBuilderChatCopy,
  reason?: unknown,
) => {
  // reason 서브코드 우선 — 같은 code 가 여러 상황(빈 파일/자막 없음 등)을
  // 덮으므로 reason 등록분은 더 구체적인 카피로 렌더한다 (RFC #412).
  if (typeof reason === 'string' && reason.length > 0) {
    const reasonMessage =
      copy.serverError.byReason[
        reason as keyof typeof copy.serverError.byReason
      ];

    if (reasonMessage) {
      return reasonMessage;
    }
  }

  if (!isServerAiBuilderChatErrorCode(code)) {
    return copy.serverError.fallback;
  }

  const message =
    copy.serverError.byCode[code as keyof typeof copy.serverError.byCode];

  return message ?? copy.serverError.fallback;
};

export const getServerAiBuilderChatErrorMessageFromError = (
  error: unknown,
  copy: AiBuilderChatCopy,
) => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return null;
  }

  const reason = 'reason' in error ? error.reason : undefined;
  return getServerAiBuilderChatErrorMessage(error.code, copy, reason);
};

/**
 * notice(안내) 이벤트용 — 등록된 code 카피가 있으면 그것으로, 없으면 서버
 * message 를 그대로 쓴다. error 와 달리 미등록 code 를 fallback 카피로
 * 덮지 않는다 (notice 는 의미 있는 서버 안내문이 다양하게 내려온다).
 */
export const getServerAiBuilderChatNoticeMessage = (
  code: unknown,
  message: string,
  copy: AiBuilderChatCopy,
) => {
  if (!isServerAiBuilderChatErrorCode(code)) {
    return message;
  }

  const localized =
    copy.serverError.byCode[code as keyof typeof copy.serverError.byCode];

  return localized ?? message;
};

export const isServerAiBuilderChatErrorCode = (
  value: unknown,
): value is ServerAiBuilderChatErrorCode => {
  return (
    typeof value === 'string' &&
    SERVER_AI_BUILDER_CHAT_ERROR_CODES.includes(
      value as ServerAiBuilderChatErrorCode,
    )
  );
};
